import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import { ChevronDown } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useRollingChart, STAT_OPTIONS } from "../../hooks/use-rolling-chart";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  const month = date.toLocaleString("default", { month: "short" });
  const day = date.getDate();
  let suffix = "th";
  if (day === 1 || day === 21 || day === 31) suffix = "st";
  else if (day === 2 || day === 22) suffix = "nd";
  else if (day === 3 || day === 23) suffix = "rd";
  return `${month} ${day}${suffix}, ${date.getFullYear()}`;
};

const formatYAxis = (value) => {
  const v = value / 1000;
  return v >= 1 ? v.toFixed(3) : v.toFixed(3).substring(1);
};

const StatDropdown = ({ selectedStat, onStatChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selectedOption = STAT_OPTIONS.find((s) => s.value === selectedStat);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-white text-indigo-700 rounded-lg text-sm font-medium shadow-md hover:from-blue-700 hover:to-indigo-700 transition-all"
      >
        <span>{selectedOption?.label}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 min-w-[100px] overflow-hidden">
          {STAT_OPTIONS.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onStatChange(option.value);
                setIsOpen(false);
              }}
              className={`w-full px-4 py-2 text-left text-sm transition-colors ${
                selectedStat === option.value
                  ? "bg-blue-50 text-blue-700 font-medium"
                  : "text-gray-700 hover:bg-gray-50"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const ChartSkeleton = () => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5 animate-pulse">
    <div className="mb-4 pb-3 border-b border-gray-100">
      <div className="h-5 sm:h-6 bg-gray-200 rounded w-1/2 sm:w-1/3 mb-3" />
      <div className="grid grid-cols-2 sm:flex sm:flex-wrap mt-2 gap-2 sm:gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col">
            <div className="h-2.5 sm:h-3 bg-gray-200 rounded w-16 sm:w-20 mb-1" />
            <div className="h-5 sm:h-6 bg-gray-300 rounded w-12 sm:w-16" />
          </div>
        ))}
      </div>
    </div>
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-5 gap-3 sm:gap-0">
      <div className="flex bg-gray-200 rounded-lg h-8 sm:h-9 w-full sm:w-64" />
      <div className="h-4 bg-gray-200 rounded w-20 sm:w-24 self-end" />
    </div>
    <div className="mb-3">
      <div className="h-3.5 sm:h-4 bg-gray-200 rounded w-2/3 sm:w-48" />
    </div>
    <div className="h-48 sm:h-64 lg:h-72 bg-gray-100 rounded-md" />
  </div>
);

const EmptyState = ({ message, suggestion }) => (
  <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-6 text-center h-48 sm:h-64 flex flex-col justify-center">
    <div className="text-sm sm:text-base text-gray-600 mb-2">{message}</div>
    {suggestion && <div className="text-xs sm:text-sm text-gray-500">{suggestion}</div>}
  </div>
);

const ChartHeader = ({ selectedStat, setSelectedStat }) => (
  <div className="flex items-center gap-3">
    <div className="flex flex-col leading-tight">
      <span className="text-[11px] uppercase tracking-wider text-gray-500">Performance Analysis</span>
      <div className="flex items-center gap-2">
        <span className="text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent select-none">
          Rolling
        </span>
        <StatDropdown selectedStat={selectedStat} onStatChange={setSelectedStat} />
        <span className="text-base font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent select-none">
          Chart
        </span>
      </div>
    </div>
  </div>
);

const StatSummary = ({ currentValue, careerValue, leagueAvg, lastPA, selectedStatOption }) => (
  <div className="grid grid-cols-2 sm:flex sm:flex-wrap mt-3 gap-3 sm:gap-4">
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">CURRENT {selectedStatOption.label}</span>
      <span className="text-sm sm:text-lg font-semibold text-gray-800">
        {(currentValue || 0).toFixed(3)}
      </span>
    </div>
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">CAREER {selectedStatOption.label}</span>
      <span className="text-sm sm:text-lg font-semibold text-gray-800">
        {(careerValue || 0).toFixed(3)}
      </span>
    </div>
    <div className="flex flex-col">
      <span className="text-xs text-gray-500">LEAGUE AVG</span>
      <span className="text-sm sm:text-lg font-semibold text-gray-600">
        {(leagueAvg || 0).toFixed(3)}
      </span>
    </div>
    {lastPA && (
      <div className="flex flex-col">
        <span className="text-xs text-gray-500">LAST PA</span>
        <span className="text-sm sm:text-lg font-semibold text-gray-800">{formatDate(lastPA)}</span>
      </div>
    )}
  </div>
);

export const RollingChart = ({
  playerId,
  playerType = "batter",
  initialWindow = 25,
  minRequiredDataPoints = 10,
}) => {
  const {
    rollingData,
    isLoading,
    rollingWindow,
    setRollingWindow,
    lastPA,
    currentValue,
    careerValue,
    isDataSufficient,
    selectedStat,
    setSelectedStat,
    selectedStatOption,
    leagueAvg,
  } = useRollingChart({ playerId, playerType, initialWindow, minRequiredDataPoints });

  const processedData = rollingData.map((item) => ({
    ...item,
    rolling_value_display: Math.round((item.rolling_value || 0) * 1000),
    game_date_formatted: formatDate(item.game_date),
  }));

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-2 sm:p-3 shadow-lg border border-gray-200 rounded-md max-w-xs">
          <p className="text-xs sm:text-sm font-medium">PA #{label}</p>
          <p className="text-xs sm:text-sm text-gray-700">
            {selectedStatOption.label}:{" "}
            <span className="font-semibold">{formatYAxis(payload[0].value)}</span>
          </p>
          {data.game_date_formatted && (
            <p className="text-xs text-gray-500 mt-1">{data.game_date_formatted}</p>
          )}
        </div>
      );
    }
    return null;
  };

  if (isLoading) return <ChartSkeleton />;
  if (!playerId) return <EmptyState message="No player selected." suggestion="Please select a player to view their performance data." />;
  if (!rollingData.length) return <EmptyState message="No performance data available for this player." suggestion="This player may be new or hasn't recorded enough plate appearances yet." />;

  if (!isDataSufficient) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between mb-4 border-b border-gray-100 pb-3">
          <ChartHeader selectedStat={selectedStat} setSelectedStat={setSelectedStat} />
        </div>
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-yellow-700">
            Limited data available ({rollingData.length} PAs). At least {minRequiredDataPoints} PAs are recommended for a meaningful rolling chart.
          </p>
        </div>
        <StatSummary
          currentValue={currentValue}
          careerValue={careerValue}
          leagueAvg={leagueAvg}
          lastPA={lastPA}
          selectedStatOption={selectedStatOption}
        />
        <div className="mb-4 mt-4">
          <h4 className="text-xs font-semibold text-gray-600 tracking-wide uppercase mb-2">Available Performance Data</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PA #</th>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-2 sm:px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{selectedStatOption.label}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rollingData.slice(0, 10).map((item) => (
                  <tr key={item.pa_number}>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs sm:text-sm text-gray-800">{item.pa_number}</td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs sm:text-sm text-gray-600">{formatDate(item.game_date)}</td>
                    <td className="px-2 sm:px-3 py-2 whitespace-nowrap text-xs sm:text-sm font-medium">{(item.raw_value || 0).toFixed(3)}</td>
                  </tr>
                ))}
                {rollingData.length > 10 && (
                  <tr>
                    <td colSpan="3" className="px-2 sm:px-3 py-2 text-xs sm:text-sm text-gray-500 text-center">
                      ...and {rollingData.length - 10} more
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5">
      <div className="flex flex-wrap items-center justify-between mb-4 border-b border-gray-100 pb-3">
        <ChartHeader selectedStat={selectedStat} setSelectedStat={setSelectedStat} />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-5 gap-3 sm:gap-0">
        <div className="flex items-center">
          <div className="flex bg-gray-100 rounded-lg p-0.5 sm:p-1 w-full sm:w-auto">
            {[25, 50, 100].map((w) => (
              <button
                key={w}
                onClick={() => rollingData.length >= w && setRollingWindow(w)}
                disabled={rollingData.length < w}
                className={`flex-1 sm:flex-none px-2 sm:px-4 py-1 sm:py-1.5 text-xs sm:text-sm font-medium rounded-md transition-all ${
                  rollingWindow === w
                    ? "bg-blue-600 text-white shadow-sm"
                    : "bg-transparent text-gray-700 hover:bg-gray-200"
                } ${rollingData.length < w ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                {w} PAs
              </button>
            ))}
          </div>
        </div>
        <div className="text-xs text-gray-500 text-right sm:text-left">
          {rollingData.length} total PAs
        </div>
      </div>

      <div className="h-48 sm:h-64 lg:h-72 relative">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={processedData} margin={{ top: 10, right: 40, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" vertical={false} />
            <XAxis dataKey="pa_number" hide={true} />
            <YAxis
              tickFormatter={formatYAxis}
              domain={["auto", "auto"]}
              axisLine={false}
              tickLine={false}
              stroke="#9ca3af"
              fontSize={12}
              width={25}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={leagueAvg * 1000}
              stroke="#9ca3af"
              strokeDasharray="3 3"
              strokeWidth={1.5}
              label={{ position: "right", value: "LG AVG", fill: "#9ca3af", fontSize: 11 }}
            />
            <Line
              type="monotone"
              dataKey="rolling_value_display"
              stroke="#1E88E5"
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 5, stroke: "#dc2626", fill: "#fff", strokeWidth: 2 }}
              animationDuration={1000}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
